import React from 'react'
import { getLink as getLinkFunc } from '../events/helpers'

import { normalize } from 'normalizr'
import groupSchema from '../../../admin/schemas/group-schema'
import { FRONT_MERGE_ENTITIES } from '../../ducks/entities'
import store from '../../../admin/store'

/**
 * Returns url query parameter.
 * USAGE: getUrlParameter('lorem')
 *
 * @param name
 * @returns {*}
 */
export const getUrlParameter = name => {
    name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]')
    let regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
    let results = regex.exec(window.location.search)
    return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '))
}


/**
 * Add schema to link if not exists
 *
 * @param link
 * @param addInstagram  - add instagram host to url?
 * @returns {string}
 */
export const getLink = (link, addInstagram = false) => {
    return getLinkFunc(link, addInstagram)
}

/**
 * Return null if is text string null
 *
 * @param text
 * @returns {null}
 */
export const skipNull = text => text === 'NULL' ? null : text

/**
 * Return image element
 *
 * @param image
 * @param alt
 * @returns {XML}
 */
export const getImage = (image, alt, defaultStyle = {}) => {
    if (! image) {
        return <span/>
    }

    let path = image.hasOwnProperty('path') ? image.path : image.id + image.suffix

    if (path) {
        path = image.hasOwnProperty('path') ? image.path : window.APP_CONFIG.REACT_APP_IMAGE_STORAGE_URL + path
    } else {
        path = image
    }

    return <img src={path} alt={alt} style={defaultStyle} />
}

/**
 * Get document height
 *
 * @returns {number}
 */
export const getDocHeight = () => Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight
)

/**
 * Set text to defined lines size
 *
 * @param lines
 * @param element
 */
export const truncateTextToLines = (lines, element) => {
    const truncateText = element.textContent

    const getLineHeight = element => {
        const lineHeight = window.getComputedStyle(element)['line-height']
        if (lineHeight === 'normal') {
            // fuck chrome
            return 1.16 * parseFloat(window.getComputedStyle(element)['font-size'])
        } else {
            return parseFloat(lineHeight)
        }
    }

    element.innerHTML = truncateText
    const truncateTextParts = truncateText.split(' ')
    const lineHeight = getLineHeight(element)

    while(lines * lineHeight < element.clientHeight) {
        truncateTextParts.pop()
        element.innerHTML = truncateTextParts.join(' ') + '...'
    }
}

/**
 * Check if user is event manager
 *
 * @param user
 * @param group
 * @returns {*}
 */
export const isManager = (user, group) => {
    if (!user || !group || !group.managers) {
        return false
    }

    return group.managers && group.managers.data.some(manager => manager.id === user.id)
}

/**
 * Check if user is invited
 *
 * @param user
 * @param group
 * @returns {*}
 */
export const isInvited = (user, group) => {
    if (!user || !group || !group.invitedMembers) {
        return false
    }

    return group.invitedMembers && group.invitedMembers.data.some(invitation => invitation.proposed_user_id === user.id)
}

/**
 * Check if user is member
 *
 * @param user
 * @param group
 * @returns {*}
 */
export const isMember = (user, group) => {
    if (!user || !group || !group.members) {
        return false
    }

    return group.members && group.members.data && group.members.data.some(member => member.user_id === user.id)
}

/**
 * Check if user cheer
 *
 * @param user
 * @param group
 * @returns {*}
 */
export const isCheering = (user, group) => {
    if (!user || !group || !group.cheers) {
        return false
    }

    return group.cheers && group.cheers.data.some(cheer => cheer.id === user.id)
}

export const mapGroupHeaderPropsToObject = (authedUser, group) => {
    const members = group.members ? group.members.data : []
    const membershipRequests = group.membershipRequests ? group.membershipRequests.data : []

    const myMembership = authedUser ? members.find(member => member.user_id === authedUser.id || member.proposed_user_id === authedUser.id) : null
    const myMembershipRequest = authedUser ? membershipRequests.find(request => request.user_id === authedUser.id) : null

    // user can become member if the group accepts people and there is no profile or request linked in any way to him
    const canBecomeMember = group.accept_people
        && !group.user_has_membership
        && !myMembershipRequest

    return {
        canBecomeMember,
        authedUser,
        myMembership,
        myMembershipRequest,
    }
}

/**
 * Load group from API
 *
 * @deprecated Transform result data into store like into events
 * @param {string} slug
 * @param {Object|null} authedUser
 * @return {Promise<any>}
 */
export const loadGroup = (slug, authedUser = null) => {

    let includes = [
        'locations',
        'sports',
        'image',
        'websites',
        'cheers',
        'headerImage',
        'hotnews:limit(3|0):order(created|desc)',
        'hotnews.simpleUser',
        'groupPath',
        'contacts',
        'members:active',
        'descriptions:only_active:only_slugs_sections',
        'recruitment',
        'previewCheers',
        'previewMembers',
        'additionalInfo',
        'links',
        'viewableCountEvents',
    ]

    if (authedUser) {
        includes.push(`membershipRequests:user_id(${authedUser.id})`)
        includes.push(`managers:user_id(${authedUser.id})`)
    }

    const requests = [
        window.axios.get(`${window.APP_CONFIG.REACT_APP_API_URL}api/groups/${slug}?include=${includes.join(',')}`),
        window.axios.get(`${window.APP_CONFIG.REACT_APP_API_URL}api/groups/${slug}/closest-tree`),
    ]

    return window.axios.all(requests)
        .then(data => {
            const group = data[0].data.data
            const tree = data?.[1]?.data
            const isGroupManager = authedUser && group && group.managers && group.managers.data.some(manager => manager.id === authedUser.id)
            const normalizedData = normalize(data[0].data.data, groupSchema)

            const userStatus = group.additionalInfo ? group.additionalInfo.data.userStatus : null

            store.dispatch({
                type: 'SET_USER_IS_CHEER',
                userIsCheer: userStatus && userStatus.isCheering,
            })

            store.dispatch({
                type: FRONT_MERGE_ENTITIES,
                entities: normalizedData.entities,
            })

            store.dispatch({
                type: 'SET_CHEER_ENTITY',
                entity: normalizedData.entities.groups[slug],
            })

            store.dispatch({
                type: 'SET_CHEER_ENTITY',
                entity: {
                    ...group,
                    cheers_count: group?.cheers_count,
                },
            })

            const groupData = {
                group,
                tree,
                isGroupManager,
                ...mapGroupHeaderPropsToObject(authedUser, data[0].data.data),
            }

            return new Promise(resolve => resolve(groupData))
        })
        .catch(error => new Promise(reject => reject({ httpStatus: error.status })))
}
